/**
 * useVerpflegung — Hook fuer Mitarbeiterverpflegungs-Modul
 * Verzehr-Tracking (Strichliste) + Beleg-Verwaltung + Monats-Auswertung
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import type { VerpflegungBeleg, VerpflegungVerzehr, VerpflegungFirma } from '@/lib/database.types';
import { localIsoDate } from '@/lib/date-utils';

function isTableNotFound(error: any): boolean {
  if (!error) return false;
  const msg = error.message || error.code || '';
  return msg.includes('PGRST205') || msg.includes('schema cache') || msg.includes('relation') || msg.includes('does not exist');
}

// ─── VERZEHR (Mitarbeiter-Strichliste) ───────────────────────────────────────

export function useVerzehr(mitarbeiterId: string | undefined, firma: VerpflegungFirma) {
  const [verzehrListe, setVerzehrListe] = useState<VerpflegungVerzehr[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchVerzehr = useCallback(async () => {
    if (!mitarbeiterId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('verpflegung_verzehr')
        .select('*')
        .eq('mitarbeiter_id', mitarbeiterId)
        .eq('firma', firma)
        .order('datum', { ascending: false })
        .limit(60);
      if (error) {
        if (isTableNotFound(error)) {
          setVerzehrListe([]);
        } else {
          throw error;
        }
      } else {
        setVerzehrListe((data || []) as VerpflegungVerzehr[]);
      }
    } catch {
      setVerzehrListe([]);
    } finally {
      setLoading(false);
    }
  }, [mitarbeiterId, firma]);

  useEffect(() => { fetchVerzehr(); }, [fetchVerzehr]);

  const heuteErfasst = verzehrListe.some(
    v => v.datum === localIsoDate(new Date())
  );

  const erfasseVerzehr = useCallback(async (datum?: string) => {
    if (!mitarbeiterId) return false;
    const tag = datum || localIsoDate(new Date());
    try {
      const { error } = await supabase
        .from('verpflegung_verzehr')
        .insert({ mitarbeiter_id: mitarbeiterId, datum: tag, firma, typ: 'mittagessen' });
      if (error) {
        if (error.code === '23505') return false; // UNIQUE violation = already logged
        throw error;
      }
      await fetchVerzehr();
      return true;
    } catch {
      return false;
    }
  }, [mitarbeiterId, firma, fetchVerzehr]);

  const loescheVerzehr = useCallback(async (id: string) => {
    try {
      const { error } = await supabase
        .from('verpflegung_verzehr')
        .delete()
        .eq('id', id);
      if (error) throw error;
      await fetchVerzehr();
      return true;
    } catch {
      return false;
    }
  }, [fetchVerzehr]);

  return { verzehrListe, loading, heuteErfasst, erfasseVerzehr, loescheVerzehr, refetch: fetchVerzehr };
}

// ─── BELEGE (Admin) ──────────────────────────────────────────────────────────

export function useBelege(firma: VerpflegungFirma) {
  const [belege, setBelege] = useState<VerpflegungBeleg[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchBelege = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('verpflegung_belege')
        .select('*')
        .eq('firma', firma)
        .order('datum', { ascending: false })
        .limit(200);
      if (error) {
        if (isTableNotFound(error)) {
          setBelege([]);
        } else {
          throw error;
        }
      } else {
        setBelege((data || []) as VerpflegungBeleg[]);
      }
    } catch {
      setBelege([]);
    } finally {
      setLoading(false);
    }
  }, [firma]);

  useEffect(() => { fetchBelege(); }, [fetchBelege]);

  const erstelleBeleg = useCallback(async (beleg: {
    datum: string;
    firma: VerpflegungFirma;
    betrag_mahlzeiten: number;
    betrag_aufmerksamkeiten: number;
    beleg_url?: string;
    notizen?: string;
    hochgeladen_von?: string;
  }) => {
    try {
      const { error } = await supabase
        .from('verpflegung_belege')
        .insert(beleg);
      if (error) throw error;
      await fetchBelege();
      return true;
    } catch {
      return false;
    }
  }, [fetchBelege]);

  const loescheBeleg = useCallback(async (id: string) => {
    try {
      const { error } = await supabase
        .from('verpflegung_belege')
        .delete()
        .eq('id', id);
      if (error) throw error;
      await fetchBelege();
      return true;
    } catch {
      return false;
    }
  }, [fetchBelege]);

  return { belege, loading, erstelleBeleg, loescheBeleg, refetch: fetchBelege };
}

// ─── ADMIN AUSWERTUNG ────────────────────────────────────────────────────────

export interface MonatsAuswertungRow {
  mitarbeiter_id: string;
  mitarbeiter_name: string;
  anzahl_mahlzeiten: number;
  sachbezugswert: number;
}

export function useMonatsAuswertung(firma: VerpflegungFirma, monat: string) {
  // monat format: 'YYYY-MM'
  const [rows, setRows] = useState<MonatsAuswertungRow[]>([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const startDate = `${monat}-01`;
      const [y, m] = monat.split('-').map(Number);
      const endDate = localIsoDate(new Date(y, m, 0)); // last day of month

      // Fetch all verzehr for the month + firma
      const { data: verzehrData, error: verzehrError } = await supabase
        .from('verpflegung_verzehr')
        .select('*')
        .eq('firma', firma)
        .gte('datum', startDate)
        .lte('datum', endDate);

      if (verzehrError) {
        if (isTableNotFound(verzehrError)) {
          setRows([]);
          return;
        }
        throw verzehrError;
      }

      // Fetch mitarbeiter names
      const { data: mitarbeiterData } = await supabase
        .from('mitarbeiter')
        .select('id, vorname, nachname');

      const mitarbeiterMap = new Map<string, string>();
      (mitarbeiterData || []).forEach((m: any) => {
        mitarbeiterMap.set(m.id, `${m.vorname} ${m.nachname}`);
      });

      // Group by mitarbeiter
      const countMap = new Map<string, number>();
      (verzehrData || []).forEach((v: any) => {
        countMap.set(v.mitarbeiter_id, (countMap.get(v.mitarbeiter_id) || 0) + 1);
      });

      const SACHBEZUGSWERT = 4.57;
      const result: MonatsAuswertungRow[] = Array.from(countMap.entries()).map(([mid, count]) => ({
        mitarbeiter_id: mid,
        mitarbeiter_name: mitarbeiterMap.get(mid) || mid,
        anzahl_mahlzeiten: count,
        sachbezugswert: Math.round(count * SACHBEZUGSWERT * 100) / 100,
      }));

      result.sort((a, b) => a.mitarbeiter_name.localeCompare(b.mitarbeiter_name));
      setRows(result);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [firma, monat]);

  useEffect(() => { fetch(); }, [fetch]);

  return { rows, loading, refetch: fetch };
}

// ─── BELEG UPLOAD ────────────────────────────────────────────────────────────

export async function uploadBelegDatei(file: File): Promise<string | null> {
  const ext = file.name.split('.').pop() || 'jpg';
  const path = `belege/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
  try {
    const { error } = await supabase.storage
      .from('belege_verpflegung')
      .upload(path, file);
    if (error) throw error;
    const { data } = supabase.storage.from('belege_verpflegung').getPublicUrl(path);
    return data.publicUrl;
  } catch {
    return null;
  }
}
